import { AsyncEmptyFunction, EmptyFunction } from '@utils/common-types';
import React from 'react';
import { DateTime } from 'luxon';
import { RecordActivityView } from '@views';
import { useAddActivityAction } from '@actions';
import { GoalStatus } from '@app/enums/GoalStatus';
import { GoalWithStatus } from '@components';

export const useConfirmGoalCompletionDialogController = (cohortUserId: number, refresh: EmptyFunction) => {
  const [confirmGoalDialogOpen, setConfirmGoalDialogOpen] = React.useState(false);
  const [selectedGoal, setSelectedGoal] = React.useState<GoalWithStatus>();
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [weekCompleted, setWeekCompleted] = React.useState(false);

  const checkAllGoalsComplete = async () => {
    // Get fresh goals data
    const newData = await refresh();
    const freshView = (newData as any)?.data ?? newData;
    const newGoalsWithProgress = freshView.goalsWithProgress;
    const { goals, progress } = newGoalsWithProgress as {
      goals: Array<{ id: number; frequency: number }>;
      progress: Record<string, number[]>;
    };

    // Check if all goals are complete
    const allGoalsComplete =
      goals.length > 0 &&
      goals.every((g) => {
        const goalProgress = progress?.[g.id] ?? [];
        const succeeded = goalProgress.filter((p: GoalStatus) => p === GoalStatus.Succeeded).length;
        return succeeded >= g.frequency;
      });
    setWeekCompleted(allGoalsComplete);
  };

  const { mutateAsync: addActivityAction } = useAddActivityAction();

  const addActivity: AsyncEmptyFunction = async () => {
    if (selectedGoal) {
      const item = {
        activityId: selectedGoal.goal.activity,
        cohortUserId: cohortUserId,
        dateStamp: DateTime.now(),
        duration: selectedGoal.goal.duration,
        timeframeId: selectedGoal.goal.timeframe,
        name: selectedGoal.goal.goalText,
        goalId: selectedGoal.goal.id,
      } as RecordActivityView;
      // Save the activity to the DB
      await addActivityAction(item);

      setSelectedGoal({ ...selectedGoal });
      setConfirmGoalDialogOpen(false);

      // Check if all goals are now complete
      await checkAllGoalsComplete();

      refresh();
    }
  };
  const cancelConfirmDialog = (selected: GoalWithStatus, idx: number) => {
    if (selected.progress[idx]) {
      selected.progress[idx] = GoalStatus.Unknown;
    }

    setSelectedGoal({ ...selected });
    setConfirmGoalDialogOpen(false);
    refresh();
  };
  const openConfirmDialog = (selected: GoalWithStatus, idx: number) => {
    if (!selected.progress) {
      selected.progress = [...Array(selected.goal.frequency)].map(() => GoalStatus.Unknown);
    }
    selected.progress[idx] = GoalStatus.Succeeded;
    setSelectedGoal({ ...selected });
    setConfirmGoalDialogOpen(true);
  };

  return {
    confirmGoalDialogOpen,
    cancelConfirmDialog,
    openConfirmDialog,
    selectedGoal,
    setSelectedGoal,
    alertOpen,
    setAlertOpen,
    addActivity,
    weekCompleted,
  };
};
